import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ErrorState } from '@ui-kit';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { LanguageToggle } from '../../_shared/LanguageToggle';
import { APP_VERSION } from '../../_shared/appVersion';
import { usePasswordResetVerify } from '../hooks/usePasswordResetVerify';
import { usePasswordResetConfirm } from '../hooks/usePasswordResetConfirm';
import {
  passwordResetConfirmSchema,
  type PasswordResetConfirmFormValues,
} from '../schema/passwordReset.schema';
import type { PasswordStrength } from '../types/passwordReset.types';

// ─── Password Strength Meter ─────────────────────────────────────────────────

function calcStrength(password: string): PasswordStrength {
  if (!password || password.length < 4) return 'weak';
  let score = 0;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (password.length >= 16) score++;

  if (score <= 2) return 'weak';
  if (score === 3) return 'medium';
  if (score === 4) return 'strong';
  return 'veryStrong';
}

// STRENGTH_CONFIG labels are resolved via t() at render time — see PasswordStrengthMeter
const STRENGTH_CONFIG: Record<
  PasswordStrength,
  { bars: number; color: string; labelKey: string }
> = {
  weak:       { bars: 1, color: 'var(--color-danger)',  labelKey: 'pwReset.strength.weak' },
  medium:     { bars: 2, color: 'var(--color-warning)', labelKey: 'pwReset.strength.medium' },
  strong:     { bars: 3, color: 'var(--color-success)', labelKey: 'pwReset.strength.strong' },
  veryStrong: { bars: 4, color: 'var(--color-success)', labelKey: 'pwReset.strength.veryStrong' },
};

function PasswordStrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation();
  const strength = useMemo(() => calcStrength(password), [password]);
  const config = STRENGTH_CONFIG[strength];
  if (!password) return null;
  const label = t(config.labelKey);

  return (
    <div aria-label={`${t('pwReset.strength.label')}: ${label}`} style={{ marginTop: 'var(--spacing-2)' }}>
      <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: 'var(--radius-full)',
              background:
                n <= config.bars ? config.color : 'var(--color-surface-border)',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>
      <span
        style={{
          display: 'block',
          fontSize: 'var(--font-size-xs)',
          color: config.color,
          marginTop: 'var(--spacing-1)',
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Countdown timer ─────────────────────────────────────────────────────────

function useCountdown(expiresAt: string | undefined) {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      setRemaining(Math.max(0, diff));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const isExpired = remaining === 0;

  return { minutes, seconds, isExpired };
}

// ─── Skeleton (loading state) ────────────────────────────────────────────────

function ConfirmSkeleton() {
  const { t } = useTranslation();
  const skLine = (w: string, h: string = '14px', mb: string = 'var(--spacing-2)') => (
    <div
      aria-hidden="true"
      style={{
        width: w,
        height: h,
        borderRadius: 'var(--radius-md)',
        marginBottom: mb,
        background: 'var(--color-surface-elevated)',
        animation: 'shimmer 1.5s infinite linear',
      }}
    />
  );

  return (
    <div aria-label={t('pwReset.common.loading')} aria-busy="true">
      {skLine('60%', '14px', 'var(--spacing-3)')}
      {skLine('80%', '14px', 'var(--spacing-6)')}
      {skLine('40%', '14px', 'var(--spacing-1)')}
      {skLine('100%', '38px', 'var(--spacing-4)')}
      {skLine('40%', '14px', 'var(--spacing-1)')}
      {skLine('100%', '38px', 'var(--spacing-4)')}
      {skLine('100%', '44px', 'var(--spacing-4)')}
    </div>
  );
}

// ─── PasswordResetConfirmPage ─────────────────────────────────────────────────

export function PasswordResetConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const verifyQuery = usePasswordResetVerify(token, true);
  const confirmMutation = usePasswordResetConfirm();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordResetConfirmFormValues>({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const watchedPassword = watch('newPassword');

  // register returns its own ref; we merge with firstInputRef via callback
  const { ref: registerNewPasswordRef, ...registerNewPassword } = register('newPassword');
  const mergedRef = useCallback(
    (el: HTMLInputElement | null) => {
      registerNewPasswordRef(el);
      (firstInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    },
    [registerNewPasswordRef]
  );

  // Focus first input when form appears
  useEffect(() => {
    if (!verifyQuery.isLoading && verifyQuery.data?.valid) {
      firstInputRef.current?.focus();
    }
  }, [verifyQuery.isLoading, verifyQuery.data?.valid]);

  // Navigate to success page on confirm success
  useEffect(() => {
    if (confirmMutation.isSuccess) {
      navigate('/password-reset/success', { replace: true });
    }
  }, [confirmMutation.isSuccess, navigate]);

  const { minutes, seconds } = useCountdown(verifyQuery.data?.expiresAt);

  const onSubmit = useCallback(
    (values: PasswordResetConfirmFormValues) => {
      confirmMutation.mutate({ token, newPassword: values.newPassword });
    },
    [confirmMutation, token]
  );

  const handleRequestAgain = useCallback(() => {
    navigate('/password-reset/request');
  }, [navigate]);

  // ── No token in URL ──
  if (!token) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--color-surface-background)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-8)',
        }}
      >
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <ErrorState
            title={t('pwReset.confirm.error.invalidLink')}
            message={t('pwReset.confirm.error.invalidLinkDetail')}
            onRetry={handleRequestAgain}
          />
        </div>
        {/* Footer — always present (R7-043) */}
        <div
          aria-label={t('pwReset.footer.aria')}
          style={{
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            gap: 'var(--spacing-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            whiteSpace: 'nowrap' as const,
            flexWrap: 'wrap' as const,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span>v{APP_VERSION}</span>
          <span>{t('pwReset.footer.copyright')}</span>
        </div>
      </div>
    );
  }

  // ── Determine verify error code ──
  const verifyErrorCode =
    verifyQuery.isError
      ? (verifyQuery.error as { code?: string })?.code ?? ''
      : '';

  const isExpiredError =
    verifyErrorCode === 'AUTH-410-001' ||
    verifyErrorCode === 'HTTP-410';
  const isAlreadyUsedError =
    verifyErrorCode === 'AUTH-409-001' ||
    verifyErrorCode === 'HTTP-409';
  const isInvalidError =
    verifyErrorCode === 'AUTH-404-002' ||
    verifyErrorCode === 'HTTP-404';

  const getVerifyErrorMessage = () => {
    if (isExpiredError)
      return {
        title: t('pwReset.confirm.error.expired'),
        detail: t('pwReset.confirm.error.expiredDetail'),
      };
    if (isAlreadyUsedError)
      return {
        title: t('pwReset.confirm.error.alreadyUsed'),
        detail: t('pwReset.confirm.error.alreadyUsedDetail'),
      };
    if (isInvalidError)
      return {
        title: t('pwReset.confirm.error.invalid'),
        detail: t('pwReset.confirm.error.invalidDetail'),
      };
    return {
      title: t('pwReset.confirm.error.verifyFailed'),
      detail:
        (verifyQuery.error as { message?: string })?.message ??
        t('pwReset.confirm.error.generic'),
    };
  };

  // ── Determine confirm error ──
  const confirmErrorCode =
    confirmMutation.isError
      ? (confirmMutation.error as { code?: string })?.code ?? ''
      : '';
  const isPending = confirmMutation.isPending;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-8)',
        position: 'relative',
      }}
      aria-busy={verifyQuery.isLoading || isPending}
    >
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          padding: 'var(--spacing-8)',
          width: '100%',
          maxWidth: '420px',
          position: 'relative',
        }}
        role="main"
        aria-label={t('pwReset.confirm.formAriaLabel')}
      >
        {/* Theme + Language toggles */}
        <div
          style={{
            position: 'absolute',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <LanguageToggle size="sm" />
          <ThemeToggle />
        </div>

        {/* Logo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '48px',
              height: '48px',
              background: 'var(--color-brand-primary)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              fontWeight: 800,
              fontSize: 'var(--font-size-lg)',
              marginRight: 'var(--spacing-3)',
              flexShrink: 0,
            }}
          >
            E
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 800,
              color: 'var(--color-brand-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            eraf
          </span>
        </div>

        <h1
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-2)',
          }}
        >
          {t('pwReset.confirm.title')}
        </h1>

        {/* ── Loading state (Skeleton) ── */}
        {verifyQuery.isLoading && (
          <div style={{ marginTop: 'var(--spacing-4)' }}>
            <ConfirmSkeleton />
          </div>
        )}

        {/* ── Error state: token invalid/expired/used ── */}
        {verifyQuery.isError && !verifyQuery.isLoading && (
          <div style={{ marginTop: 'var(--spacing-4)' }}>
            <ErrorState
              title={getVerifyErrorMessage().title}
              message={getVerifyErrorMessage().detail}
              onRetry={handleRequestAgain}
            />
            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
              <button
                type="button"
                onClick={handleRequestAgain}
                aria-label={t('pwReset.confirm.button.requestAgainAriaLabel')}
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-brand-primary)',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  fontFamily: 'var(--font-family-sans)',
                  padding: 0,
                }}
              >
                ← {t('pwReset.confirm.button.requestAgain')}
              </button>
            </div>
          </div>
        )}

        {/* ── Default / form state: token valid ── */}
        {verifyQuery.isSuccess && verifyQuery.data?.valid && (
          <>
            <p
              style={{
                textAlign: 'center',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                margin: '0 0 var(--spacing-4)',
              }}
            >
              {t('pwReset.confirm.subtitle')}
            </p>

            {/* Masked email + countdown banner */}
            <div
              style={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-3)',
                marginBottom: 'var(--spacing-4)',
                textAlign: 'center',
              }}
              aria-label={t('pwReset.confirm.expiryBannerAriaLabel')}
            >
              <div
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                {verifyQuery.data.maskedEmail}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
                aria-live="polite"
              >
                {t('pwReset.confirm.banner.expiresAt').replace('{{time}}', '')}
                <strong
                  style={{
                    color: minutes < 5 ? 'var(--color-warning)' : 'var(--color-info)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                </strong>{' '}
                {t('pwReset.confirm.banner.remaining')}
              </div>
            </div>

            {/* Confirm mutation error banner */}
            {confirmMutation.isError && (
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                id="pw-confirm-error"
                style={{
                  background:
                    'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  marginBottom: 'var(--spacing-4)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-3)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    color: 'var(--color-danger)',
                    flexShrink: 0,
                    lineHeight: 1.4,
                  }}
                >
                  ✕
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      color: 'var(--color-danger)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    {t('pwReset.confirm.error.changeTitle')}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {confirmErrorCode === 'AUTH-410-001' || confirmErrorCode === 'HTTP-410'
                      ? t('pwReset.confirm.error.expired')
                      : confirmErrorCode === 'AUTH-409-001' || confirmErrorCode === 'HTTP-409'
                      ? t('pwReset.confirm.error.alreadyUsed')
                      : (confirmMutation.error as { message?: string })?.message ??
                        t('pwReset.confirm.error.generic')}
                  </div>
                  <code
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      background: 'var(--color-surface-elevated)',
                      padding: '1px var(--spacing-1)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'inline-block',
                      marginTop: 'var(--spacing-1)',
                    }}
                  >
                    {confirmErrorCode || 'ERROR'}
                  </code>
                </div>
              </div>
            )}

            {/* Form */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              aria-describedby={confirmMutation.isError ? 'pw-confirm-error' : undefined}
            >
              {/* New password */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label
                  htmlFor="new-password"
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  {t('pwReset.confirm.newPassword.label')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="new-password"
                    ref={mergedRef}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('pwReset.confirm.newPassword.placeholder')}
                    disabled={isPending}
                    aria-invalid={!!errors.newPassword}
                    aria-describedby={
                      errors.newPassword
                        ? 'new-password-error'
                        : 'new-password-hint'
                    }
                    {...registerNewPassword}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: `var(--spacing-2) var(--spacing-8) var(--spacing-2) var(--spacing-3)`,
                      background: isPending
                        ? 'var(--color-surface-muted)'
                        : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        errors.newPassword
                          ? 'var(--color-danger)'
                          : 'var(--color-surface-border)'
                      }`,
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-family-sans)',
                      outline: 'none',
                      minHeight: '38px',
                      letterSpacing: showPassword ? 'normal' : '0.1em',
                      opacity: isPending ? 0.5 : 1,
                      cursor: isPending ? 'not-allowed' : 'text',
                    }}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? t('pwReset.confirm.hidePassword') : t('pwReset.confirm.showPassword')}
                    onClick={() => setShowPassword((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 'var(--spacing-2)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      padding: 'var(--spacing-1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>

                {/* Strength meter */}
                <PasswordStrengthMeter password={watchedPassword} />

                {errors.newPassword ? (
                  <span
                    id="new-password-error"
                    role="alert"
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-danger)',
                      marginTop: 'var(--spacing-1)',
                    }}
                  >
                    {errors.newPassword.message === 'pwReset.confirm.password.tooShort'
                      ? t('pwReset.confirm.newPassword.tooShort')
                      : errors.newPassword.message === 'pwReset.confirm.password.tooLong'
                      ? t('pwReset.confirm.newPassword.tooLong')
                      : errors.newPassword.message === 'pwReset.confirm.password.requireUpper'
                      ? t('pwReset.confirm.newPassword.requireUpper')
                      : errors.newPassword.message === 'pwReset.confirm.password.requireLower'
                      ? t('pwReset.confirm.newPassword.requireLower')
                      : errors.newPassword.message === 'pwReset.confirm.password.requireDigit'
                      ? t('pwReset.confirm.newPassword.requireDigit')
                      : errors.newPassword.message === 'pwReset.confirm.password.requireSpecial'
                      ? t('pwReset.confirm.newPassword.requireSpecial')
                      : errors.newPassword.message}
                  </span>
                ) : (
                  <span
                    id="new-password-hint"
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: 'var(--spacing-1)',
                    }}
                  >
                    {t('pwReset.confirm.newPassword.hint')}
                  </span>
                )}
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label
                  htmlFor="confirm-password"
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  {t('pwReset.confirm.confirmPassword.label')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('pwReset.confirm.confirmPassword.placeholder')}
                    disabled={isPending}
                    aria-invalid={!!errors.confirmPassword}
                    aria-describedby={
                      errors.confirmPassword ? 'confirm-password-error' : undefined
                    }
                    {...register('confirmPassword')}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: `var(--spacing-2) var(--spacing-8) var(--spacing-2) var(--spacing-3)`,
                      background: isPending
                        ? 'var(--color-surface-muted)'
                        : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        errors.confirmPassword
                          ? 'var(--color-danger)'
                          : 'var(--color-surface-border)'
                      }`,
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-family-sans)',
                      outline: 'none',
                      minHeight: '38px',
                      letterSpacing: showConfirm ? 'normal' : '0.1em',
                      opacity: isPending ? 0.5 : 1,
                      cursor: isPending ? 'not-allowed' : 'text',
                    }}
                  />
                  <button
                    type="button"
                    aria-label={showConfirm ? t('pwReset.confirm.hideConfirmPassword') : t('pwReset.confirm.showConfirmPassword')}
                    onClick={() => setShowConfirm((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 'var(--spacing-2)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      padding: 'var(--spacing-1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span
                    id="confirm-password-error"
                    role="alert"
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-danger)',
                      marginTop: 'var(--spacing-1)',
                    }}
                  >
                    {errors.confirmPassword.message === 'pwReset.confirm.confirmPassword.mismatch'
                      ? t('pwReset.confirm.confirmPassword.mismatch')
                      : errors.confirmPassword.message === 'pwReset.confirm.confirmPassword.required'
                      ? t('pwReset.confirm.confirmPassword.required')
                      : errors.confirmPassword.message}
                  </span>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isPending}
                aria-label={isPending ? t('pwReset.confirm.button.submitting') : t('pwReset.confirm.button.submit')}
                aria-disabled={isPending}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-2)',
                  width: '100%',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  cursor: isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  marginBottom: 'var(--spacing-4)',
                  opacity: isPending ? 0.6 : 1,
                }}
              >
                {isPending && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid var(--color-border-subtle)',
                      borderTopColor: 'var(--color-text-inverse)',
                      borderRadius: 'var(--radius-full)',
                      display: 'inline-block',
                      animation: 'spin 0.8s linear infinite',
                      flexShrink: 0,
                    }}
                  />
                )}
                {isPending ? t('pwReset.confirm.button.submitting') : t('pwReset.confirm.button.submit')}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        aria-label={t('pwReset.footer.aria')}
        style={{
          marginTop: 'var(--spacing-6)',
          display: 'flex',
          gap: 'var(--spacing-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap' as const,
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <span>v{APP_VERSION}</span>
        <span>{t('pwReset.footer.copyright')}</span>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer {
          0%   { opacity: 1; }
          50%  { opacity: 0.4; }
          100% { opacity: 1; }
        }
        @media (max-width: 767px) {
          form input { font-size: var(--font-size-sm); }
        }
      `}</style>
    </div>
  );
}
